import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  LockIcon,
  LogIn,
  RefreshCw,
  ShieldCheck,
  User,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

const BASE_URL = process.env.REACT_APP_BASE_URL ?? "http://localhost:3821";

export default function LoginForm() {
  const router = useNavigate();
  const usernameId = useId();
  const passwordId = useId();
  const captchaId = useId();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [captcha, setCaptcha] = useState(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Per-field error states
  const [fieldState, setFieldState] = useState({
    username: "idle",
    password: "idle",
    captcha: "idle",
  });

  //   Failed attempts counter
  const [attempts, setAttempts] = useState(0);

  // == Fetch CAPTCHA ============================================
  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer("");
    setFieldState((prev) => ({ ...prev, captcha: "idle" }));
    try {
      const res = await fetch(`${BASE_URL}/api/auth/captcha`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Gagal memuat CAPTCHA");
      const data = await res.json();
      setCaptcha(data);
    } catch (error) {
      setError("Gagal memuat CAPTCHA. Periksa koneksi Anda.");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    // Client-side basic validation
    if (username.trim().length === 0) {
      setFieldState((prev) => ({ ...prev, username: "error" }));
      setError("Username tidak boleh kosong.");
      return;
    }
    if (password.trim().length === 0) {
      setFieldState((prev) => ({ ...prev, password: "error" }));
      setError("Password tidak boleh kosong.");
      return;
    }
    if (captchaAnswer.trim().length === 0 || !captcha) {
      setFieldState((prev) => ({ ...prev, captcha: "error" }));
      setError("Jawab CAPTCHA terlebih dahulu");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
          captchaToken: captcha.token,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setAttempts((n) => n + 1);
        setError(data.message ?? data.error ?? "Login gagal");
        fetchCaptcha();
        return;
      }

      // Session cookie is already issued by the backend during login, so we
      // don't need a second request to set it from the client.
      setSuccessMsg("Login berhasil. Menghalihkan...");
      setFieldState({
        username: "success",
        password: "success",
        captcha: "success",
      });

      if (data.menu) {
        let menuData = [];
        if (data.menu) {
          menuData = data.menu.filter((item) => item.id !== null);
        } else if (Array.isArray(data.menu)) {
          menuData = data.menu.filter((item) => item.id !== null);
        }
        localStorage.setItem("menu", JSON.stringify(menuData));
      }

      if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
      if (data.accessToken) localStorage.setItem("token", data.accessToken);

      setTimeout(
        () => router(data.redirect ?? "/admincms", { replace: true }),
        800,
      );
    } catch {
      setError("Terjadi kesalahan jaringan. Coba lagi.");
      fetchCaptcha();
    } finally {
      setSubmitting(false);
    }
  }

  const isLocked = attempts >= 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className="w-full max-w-md"
    >
      {/* Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-cyan-100 overflow-hidden">
        {/* Header strip */}
        <div className="bg-teal-950 px-8 py-7">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-teal-400/20 flex items-center justify-center">
              <ShieldCheck
                className="w-5 h-5 text-teal-500"
                aria-hidden="true"
              />
            </div>
            <div>
              <p className="text-white/60 text-xs font-medium tracking-widest uppercase">
                Pesantren Idrisiyyah
              </p>
              <h1 className="text-white font-bold text-lg leading-tight">
                Portal Admin
              </h1>
            </div>
          </div>
          <p className="text-white/50 text-xs mt-3 leading-relaxed">
            Akses terbatas. Masuk dengan kredensial yang telah diberikan
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          noValidate
          className="px-8 py-7 space-y-5"
        >
          <AnimatePresence mode="wait">
            {(error || successMsg) && (
              <motion.div
                key={error ? "error" : "success"}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                role="alert"
                aria-live="assertive"
                className={clsx(
                  "flex items-start gap-2.5 rounded-lg px-4 py-3 text-sm",
                  error
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200",
                )}
              >
                {error ? (
                  <AlertCircle
                    className="w-4 h-4 mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckCircle2
                    className="w-4 h-4 mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>{error || successMsg}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lockout warning */}
          {attempts > 0 && attempts < 5 && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3">
              Percobaan gagal: {attempts}/5. Setelah 5 kali gagal, akses akan
              dikunci 15 menit.
            </p>
          )}

          {/* Username */}
          <div className="space-y-1.5">
            <label
              htmlFor={usernameId}
              className="block text-sm font-semibold text-foreground"
            >
              Username
            </label>
            <div className="relative">
              <User
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none"
              />
              <input
                type="text"
                id={usernameId}
                autoComplete="username"
                disabled={submitting || isLocked}
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setFieldState((prev) => ({ ...prev, username: "idle" }));
                }}
                maxLength={64}
                placeholder="Masukkan username"
                aria-invalid={fieldState.username === "error"}
                aria-describedby={
                  fieldState.username === "error"
                    ? `${username}-err`
                    : undefined
                }
                className={clsx(
                  "w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-all",
                  "placeholder:text-slate-500/60 disabled:opacity-50 disabled:cursor-not-allowed",
                  fieldState.username === "error"
                    ? "border-red-400 bg-red-50 focus:ring-2 focus:ring-red-300"
                    : fieldState.username === "success"
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-cyan-200 bg-background focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20",
                )}
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label
              htmlFor={passwordId}
              className="block text-sm font-semibold text-foreground"
            >
              Password
            </label>
            <div className="relative">
              <LockIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none"
                aria-hidden="true"
              />
              <input
                id={passwordId}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                disabled={submitting || isLocked}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldState((prev) => ({ ...prev, password: "idle" }));
                }}
                maxLength={128}
                placeholder="Masukkan password"
                aria-invalid={fieldState.password === "error"}
                className={clsx(
                  "w-full pl-9 pr-10 py-2.5 rounded-lg border text-sm outline-none transition-all",
                  "placeholder:text-slate-500/60 disabled:opacity-50 disabled:cursor-not-allowed",
                  fieldState.password === "error"
                    ? "border-red-400 bg-red-50 focus:ring-2 focus:ring-red-300"
                    : fieldState.password === "success"
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-cyan-200 bg-background focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20",
                )}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={
                  showPassword ? "Sembunyikan password" : "Tampilkan password"
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* CAPTCHA */}
          <div className="space-y-1.5">
            <label
              htmlFor={captchaId}
              className="block text-sm font-semibold text-foreground"
            >
              Verifikasi Keamanan
            </label>
            <div className="flex items-center gap-3">
              {/* Question display */}
              <div
                aria-label="Pertanyaan CAPTCHA"
                className={clsx(
                  "flex-1 flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 min-h-10.5",
                  "bg-slate-100 border-slate-200 select-none",
                )}
              >
                {captchaLoading ? (
                  <RefreshCw
                    className="w-4 h-4 text-slate-500 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <span
                    className="font-mono font-bold text-slate-800 text-base tracking-widest"
                    aria-live="polite"
                  >
                    {captcha?.question ?? "—"}
                  </span>
                )}
              </div>
              {/* Refresh button */}
              <button
                type="button"
                onClick={fetchCaptcha}
                disabled={captchaLoading || submitting}
                aria-label="Muat ulang CAPTCHA"
                title="Muat ulang CAPTCHA"
                className={clsx(
                  "w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200",
                  "text-slate-500 hover:text-cyan-400 hover:border-cyan-400",
                  "transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                )}
              >
                <RefreshCw
                  className={clsx("w-4 h-4", captchaLoading && "animate-spin")}
                  aria-hidden="true"
                />
              </button>
            </div>
            {/* Answer input */}
            <input
              id={captchaId}
              type="text"
              inputMode="numeric"
              disabled={submitting || isLocked || captchaLoading}
              value={captchaAnswer}
              onChange={(e) => {
                // Only allow digits — charCode check, no regex
                let filtered = "";
                for (let i = 0; i < e.target.value.length; i++) {
                  const code = e.target.value.charCodeAt(i);
                  if (code >= 48 && code <= 57) filtered += e.target.value[i];
                }
                setCaptchaAnswer(filtered);
                setFieldState((prev) => ({ ...prev, captcha: "idle" }));
              }}
              maxLength={4}
              placeholder="Ketik jawaban di sini"
              aria-label="Jawaban CAPTCHA"
              aria-invalid={fieldState.captcha === "error"}
              className={clsx(
                "w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-all",
                "placeholder:text-slate-500/60 disabled:opacity-50 disabled:cursor-not-allowed",
                fieldState.captcha === "error"
                  ? "border-red-400 bg-red-50 focus:ring-2 focus:ring-red-300"
                  : fieldState.captcha === "success"
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-cyan-200 bg-background focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20",
              )}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || isLocked || captchaLoading || !captcha}
            className={clsx(
              "w-full flex items-center justify-center gap-2 rounded-lg py-2.5 px-4",
              "bg-cyan-500 text-white font-semibold text-sm",
              "hover:bg-cyan-600 transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {submitting ? (
              <>
                <RefreshCw
                  className="w-4 h-4 animate-spin"
                  aria-hidden="true"
                />
                <span>Memproses...</span>
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" aria-hidden="true" />
                <span>Masuk ke Dashboard</span>
              </>
            )}
          </button>
        </form>

        {/* Footer note */}
        <div className="bg-slate-100 border-t border-slate-200 px-8 py-4">
          <p className="text-xs text-slate-500 text-center leading-relaxed">
            Halaman ini dilindungi enkripsi &amp; pembatasan akses.
            <br />
            Jika lupa kredensial, hubungi administrator sistem.
          </p>
        </div>
      </div>

      {/* Security badges */}
      <div className="flex items-center justify-center gap-5 mt-5">
        {[
          { icon: ShieldCheck, label: "CAPTCHA Protected" },
          { icon: Lock, label: "Rate Limited" },
          { icon: ShieldCheck, label: "HTTPOnly Cookie" },
        ].map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="flex items-center gap-1.5 text-xs text-slate-500"
          >
            <Icon className="w-3.5 h-3.5 text-imc-teal" aria-hidden="true" />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
